import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReactDOM from 'react-dom';
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Clock,
  Settings,
  Briefcase,
  Image as ImageIcon,
  Eye,
  ChevronDown,
  ChevronUp,
  Filter
} from 'lucide-react';

const ServiceManagement = () => {
  const { user, token } = useAuth();
  
  // Menu Categories (Top-level like Pet Foods, Pet Medications)
  const [menuCategories, setMenuCategories] = useState([]);
  const [activeMenuCategory, setActiveMenuCategory] = useState(null);
  
  // Service Categories (Second-level within menu categories)
  const [serviceCategories, setServiceCategories] = useState([]);
  const [selectedServiceCategory, setSelectedServiceCategory] = useState('all');
  
  // Services
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showCSVSection, setShowCSVSection] = useState(false);
  const [showCategoryOverlay, setShowCategoryOverlay] = useState(false);
  
  // Modals
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [showManageServiceCategoriesModal, setShowManageServiceCategoriesModal] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [showCSVFormatModal, setShowCSVFormatModal] = useState(false);
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  
  // Form states
  const [serviceForm, setServiceForm] = useState({
    service_name: '',
    description: '',
    duration_minutes: 30,
    menu_category_id: '',
    service_category_id: '',
    image_url: '',
    price: null,
    is_variable: false,
    variants: [],
    has_options: false,
    options: [],
    is_bookable: true,
    is_enabled: true,
    component_display_enabled: false
  });
  
  // Image handling
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [showAIImageModal, setShowAIImageModal] = useState(false);
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);
  const [aiGenerateInput, setAIGenerateInput] = useState({ name: '', description: '' });
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [aiImageInput, setAIImageInput] = useState({ name: '', description: '' });
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiImageError, setAIImageError] = useState('');
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState('');
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  
  // S3 Image Browser state
  const [s3Images, setS3Images] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Variant management - Single variant type only
  const [variantType, setVariantType] = useState('Duration'); // Single variant type name
  const [variants, setVariants] = useState([]); // List of variants
  
  const [editingService, setEditingService] = useState(null);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // 2 columns x 3 rows
  
  // Confirmation modal state (for category deletion)
  const [deleteCategoryData, setDeleteCategoryData] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteMenuCategoryModal, setShowDeleteMenuCategoryModal] = useState(false);
  const [menuCategoryToDelete, setMenuCategoryToDelete] = useState(null);
  const [showDeleteServiceCategoryModal, setShowDeleteServiceCategoryModal] = useState(false);
  const [serviceCategoryToDelete, setServiceCategoryToDelete] = useState(null);
  const [showEditMenuCategoryModal, setShowEditMenuCategoryModal] = useState(false);
  const [editingMenuCategory, setEditingMenuCategory] = useState(null);
  const [showEditServiceCategoryModal, setShowEditServiceCategoryModal] = useState(false);
  const [editingServiceCategory, setEditingServiceCategory] = useState(null);
  
  const [menuCategoryForm, setMenuCategoryForm] = useState({
    menu_category_name: '',
    is_enabled: true,
    order: 0
  });
  
  const [serviceCategoryForm, setServiceCategoryForm] = useState({
    menu_category_id: '',
    category_name: '',
    is_enabled: true,
    order: 0
  });
  
  const [csvFile, setCSVFile] = useState(null);
  const [csvImportResult, setCsvImportResult] = useState(null);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (user && token) {
      // Clear any stale active menu category on mount
      setActiveMenuCategory(null);
      setServices([]);
      fetchMenuCategories();
    }
  }, [user, token]);

  // Fetch available image models on component mount
  useEffect(() => {
    const fetchImageModels = async () => {
      try {
        const openaiResponse = await fetch(`${API_BASE_URL}/api/openai-settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (openaiResponse.ok) {
          const openaiData = await openaiResponse.json();
          if (openaiData.success && openaiData.settings && openaiData.settings.use_openai_for_all_services) {
            const imageModelsData = (openaiData.image_models || []).map(model => ({
              id: model.value || model.id,
              name: model.label || model.name
            }));
            
            setAvailableImageModels(imageModelsData);
            const defaultImageModel = openaiData.settings.image_generation_model || 'dall-e-3';
            setSelectedImageModel(defaultImageModel);

            console.log('🖼️ Loaded image models:', imageModelsData);
            console.log('🎯 Default image model:', defaultImageModel);
          }
        }
      } catch (error) {
        console.error('Error fetching image models:', error);
        setAvailableImageModels([
          { id: 'dall-e-3', name: 'DALL-E 3' }
        ]);
        setSelectedImageModel('dall-e-3');
      }
    };

    if (user && token) {
      fetchImageModels();
    }
  }, [user, token, API_BASE_URL]);

  useEffect(() => {
    if (activeMenuCategory) {
      fetchServiceCategories();
      fetchServices();
    }
  }, [activeMenuCategory]);

  useEffect(() => {
    filterServices();
  }, [services, searchTerm, selectedStatus, selectedServiceCategory]);

  const fetchMenuCategories = async () => {
    try {
      // Add cache buster to force fresh data
      const cacheBuster = `?_t=${Date.now()}`;
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-menu-categories${cacheBuster}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const categories = data.menu_categories || [];
        console.log('📂 Fetched menu categories:', categories);
        setMenuCategories(categories);
        
        // Always set to first category to ensure fresh start
        if (categories.length > 0) {
          console.log('✅ Setting active menu category to:', categories[0].menu_category_name, categories[0].id);
          setActiveMenuCategory(categories[0].id);
        } else {
          console.log('⚠️ No menu categories found');
          setActiveMenuCategory(null);
        }
      }
    } catch (error) {
      console.error('Error fetching menu categories:', error);
      showMessage('error', 'Failed to load service menu categories');
    }
  };

  const fetchServiceCategories = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/appointment-service-categories-v2?menu_category_id=${activeMenuCategory}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setServiceCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
      showMessage('error', 'Failed to load service categories');
    }
  };

  const fetchServices = async () => {
    if (!activeMenuCategory) {
      console.log('No active menu category, skipping service fetch');
      return;
    }
    
    setLoading(true);
    try {
      const cacheBuster = `&_t=${Date.now()}`;
      const url = `${API_BASE_URL}/api/appointment-services?menu_category_id=${activeMenuCategory}${cacheBuster}`;
      console.log('Fetching services from:', url);
      
      const response = await fetch(url, { 
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        } 
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Services response:', data);
        setServices(data.services || []);
        console.log(`Loaded ${(data.services || []).length} services for menu category ${activeMenuCategory}`);
      } else {
        console.error('Failed to fetch services:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      showMessage('error', 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = [...services];
    
    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (selectedStatus !== 'all') {
      if (selectedStatus === 'enabled') {
        filtered = filtered.filter(service => service.is_enabled === true);
      } else if (selectedStatus === 'disabled') {
        filtered = filtered.filter(service => service.is_enabled === false);
      }
    }
    
    if (selectedServiceCategory !== 'all') {
      filtered = filtered.filter(service => service.service_category_id === selectedServiceCategory);
    }
    
    setFilteredServices(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = filteredServices.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAddMenuCategory = async () => {
    if (!menuCategoryForm.menu_category_name) {
      showMessage('error', 'Menu category name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(menuCategoryForm)
      });
      
      if (response.ok) {
        showMessage('success', 'Menu category created successfully');
        fetchMenuCategories();
        setMenuCategoryForm({ menu_category_name: '', is_enabled: true, order: 0 });
      } else {
        showMessage('error', 'Failed to create menu category');
      }
    } catch (error) {
      console.error('Error creating menu category:', error);
      showMessage('error', 'Failed to create menu category');
    }
  };

  const handleAddServiceCategory = async () => {
    if (!serviceCategoryForm.category_name) {
      showMessage('error', 'Service category name is required');
      return;
    }
    
    const categoryData = {
      ...serviceCategoryForm,
      menu_category_id: activeMenuCategory
    };
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-categories-v2`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });
      
      if (response.ok) {
        showMessage('success', 'Service category created successfully');
        fetchServiceCategories();
        setServiceCategoryForm({ menu_category_id: '', category_name: '', is_enabled: true, order: 0 });
      } else {
        showMessage('error', 'Failed to create service category');
      }
    } catch (error) {
      console.error('Error creating service category:', error);
      showMessage('error', 'Failed to create service category');
    }
  };

  const handleEditMenuCategory = (category) => {
    setEditingMenuCategory(category);
    setMenuCategoryForm({
      menu_category_name: category.menu_category_name,
      is_enabled: category.is_enabled,
      order: category.order
    });
    setShowEditMenuCategoryModal(true);
  };

  const handleUpdateMenuCategory = async () => {
    if (!menuCategoryForm.menu_category_name) {
      showMessage('error', 'Category name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-menu-categories/${editingMenuCategory.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          menu_category_name: menuCategoryForm.menu_category_name,
          is_enabled: menuCategoryForm.is_enabled,
          order: menuCategoryForm.order
        })
      });
      
      if (response.ok) {
        showMessage('success', 'Menu category updated successfully');
        fetchMenuCategories();
        setShowEditMenuCategoryModal(false);
        setEditingMenuCategory(null);
        setMenuCategoryForm({ menu_category_name: '', is_enabled: true, order: 0 });
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to update menu category');
      }
    } catch (error) {
      console.error('Error updating menu category:', error);
      showMessage('error', 'Failed to update menu category');
    }
  };

  const handleDeleteMenuCategory = (category) => {
    setMenuCategoryToDelete(category);
    setShowDeleteMenuCategoryModal(true);
  };

  const confirmDeleteMenuCategory = async () => {
    if (!menuCategoryToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-menu-categories/${menuCategoryToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        showMessage('success', `Menu category "${menuCategoryToDelete.menu_category_name}" deleted successfully`);
        
        // If we're deleting the active category, switch to another one
        if (activeMenuCategory === menuCategoryToDelete.id) {
          const remaining = menuCategories.filter(c => c.id !== menuCategoryToDelete.id);
          if (remaining.length > 0) {
            setActiveMenuCategory(remaining[0].id);
          } else {
            setActiveMenuCategory(null);
          }
        }
        
        fetchMenuCategories();
        setShowDeleteMenuCategoryModal(false);
        setMenuCategoryToDelete(null);
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to delete menu category');
      }
    } catch (error) {
      console.error('Error deleting menu category:', error);
      showMessage('error', 'Failed to delete menu category');
    }
  };

  const handleEditServiceCategory = (category) => {
    setEditingServiceCategory(category);
    setServiceCategoryForm({
      menu_category_id: category.menu_category_id,
      category_name: category.category_name,
      is_enabled: category.is_enabled,
      order: category.order
    });
    setShowEditServiceCategoryModal(true);
  };

  const handleUpdateServiceCategory = async () => {
    if (!serviceCategoryForm.category_name) {
      showMessage('error', 'Category name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-categories-v2/${editingServiceCategory.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category_name: serviceCategoryForm.category_name,
          is_enabled: serviceCategoryForm.is_enabled,
          order: serviceCategoryForm.order
        })
      });
      
      if (response.ok) {
        showMessage('success', 'Service category updated successfully');
        fetchServiceCategories();
        setShowEditServiceCategoryModal(false);
        setEditingServiceCategory(null);
        setServiceCategoryForm({ menu_category_id: '', category_name: '', is_enabled: true, order: 0 });
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to update service category');
      }
    } catch (error) {
      console.error('Error updating service category:', error);
      showMessage('error', 'Failed to update service category');
    }
  };

  const handleDeleteServiceCategory = (category) => {
    setServiceCategoryToDelete(category);
    setShowDeleteServiceCategoryModal(true);
  };

  const confirmDeleteServiceCategory = async () => {
    if (!serviceCategoryToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-service-categories-v2/${serviceCategoryToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        showMessage('success', `Service category "${serviceCategoryToDelete.category_name}" deleted successfully`);
        fetchServiceCategories();
        fetchServices(); // Refresh services as they might be affected
        setShowDeleteServiceCategoryModal(false);
        setServiceCategoryToDelete(null);
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to delete service category');
      }
    } catch (error) {
      console.error('Error deleting service category:', error);
      showMessage('error', 'Failed to delete service category');
    }
  };

  const handleAddService = async () => {
    if (!serviceForm.service_name) {
      showMessage('error', 'Service name is required');
      return;
    }
    
    if (!serviceForm.service_category_id) {
      showMessage('error', 'Service category is required');
      return;
    }
    
    // Duration is optional - skip validation
    
    // Upload image if file is selected
    if (imageFile) {
      await handleImageUpload(imageFile);
    }
    
    // Process variants - filter out invalid ones
    const allVariants = [];
    if (serviceForm.is_variable) {
      variants.forEach(v => {
        if (v.variant_name && v.price !== null && v.price !== undefined) {
          allVariants.push({
            ...v,
            variant_type: variantType // Set the variant type for all variants
          });
        }
      });
    }
    
    const serviceData = {
      service_name: serviceForm.service_name,
      description: serviceForm.description,
      duration_minutes: serviceForm.duration_minutes,
      menu_category_id: activeMenuCategory,
      service_category_id: serviceForm.service_category_id || null,
      image_url: serviceForm.image_url,
      price: serviceForm.is_variable ? null : serviceForm.price,
      is_variable: serviceForm.is_variable,
      variants: allVariants,
      has_options: serviceForm.has_options,
      options: serviceForm.has_options ? serviceForm.options : [],
      is_available: serviceForm.is_available,
      is_enabled: serviceForm.is_enabled,
      component_display_enabled: serviceForm.component_display_enabled
    };
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-services`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(serviceData)
      });
      
      if (response.ok) {
        // Fetch updated services first before closing modal
        await fetchServices();
        
        showMessage('success', 'Service added successfully');
        setShowAddServiceModal(false);
        setServiceForm({
          service_name: '',
          description: '',
          duration_minutes: 30,
          menu_category_id: '',
          service_category_id: '',
          image_url: '',
          price: null,
          is_variable: false,
          variants: [],
          has_options: false,
          options: [],
          is_bookable: true,
          is_enabled: true,
          component_display_enabled: false
        });
        setImageFile(null);
        setVariantTypes([{ type: 'Service Tier', variants: [] }]);
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to add service');
      }
    } catch (error) {
      console.error('Error adding service:', error);
      showMessage('error', 'Failed to add service');
    }
  };

  const handleUpdateService = async () => {
    if (!serviceForm.service_name) {
      showMessage('error', 'Service name is required');
      return;
    }
    
    if (!serviceForm.service_category_id) {
      showMessage('error', 'Service category is required');
      return;
    }
    
    // Duration is optional - skip validation
    
    // Upload new image if file is selected
    if (imageFile) {
      await handleImageUpload(imageFile);
    }
    
    // Process variants - filter out invalid ones
    const allVariants = [];
    if (serviceForm.is_variable) {
      variants.forEach(v => {
        if (v.variant_name && v.price !== null && v.price !== undefined) {
          allVariants.push({
            ...v,
            variant_type: variantType // Set the variant type for all variants
          });
        }
      });
    }
    
    try {
      const updateData = {
        service_name: serviceForm.service_name,
        description: serviceForm.description,
        duration_minutes: serviceForm.duration_minutes,
        service_category_id: serviceForm.service_category_id,
        image_url: serviceForm.image_url,
        price: serviceForm.is_variable ? null : serviceForm.price,
        is_variable: serviceForm.is_variable,
        variants: allVariants,
        has_options: serviceForm.has_options,
        options: serviceForm.has_options ? serviceForm.options : [],
        is_bookable: serviceForm.is_bookable,
        is_enabled: serviceForm.is_enabled,
        component_display_enabled: serviceForm.component_display_enabled
      };
      
      const response = await fetch(`${API_BASE_URL}/api/appointment-services/${editingService.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Fetch updated services first before closing modal
        await fetchServices();
        
        showMessage('success', 'Service updated successfully');
        setShowEditServiceModal(false);
        setEditingService(null);
        setServiceForm({
          service_name: '',
          description: '',
          duration_minutes: 30,
          menu_category_id: '',
          service_category_id: '',
          image_url: '',
          price: null,
          is_variable: false,
          variants: [],
          has_options: false,
          options: [],
          is_bookable: true,
          is_enabled: true,
          component_display_enabled: false
        });
        setImageFile(null);
        setVariantTypes([{ type: 'Service Tier', variants: [] }]);
      } else {
        console.error('Service update failed:', data);
        showMessage('error', data.detail || data.message || 'Failed to update service');
      }
    } catch (error) {
      console.error('Error updating service:', error);
      showMessage('error', `Failed to update service: ${error.message}`);
    }
  };

  const handleDeleteService = (service) => {
    setServiceToDelete(service);
    setShowDeleteServiceModal(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-services/${serviceToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        showMessage('success', 'Service deleted successfully');
        setShowDeleteServiceModal(false);
        setServiceToDelete(null);
        await fetchServices();
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      showMessage('error', 'Failed to delete service');
    }
  };

  const handleToggleServiceStatus = async (service) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-services/${service.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...service,
          is_enabled: !service.is_enabled
        })
      });
      
      if (response.ok) {
        showMessage('success', `Service ${!service.is_enabled ? 'activated' : 'deactivated'} successfully`);
        await fetchServices();
      } else {
        showMessage('error', 'Failed to update service status');
      }
    } catch (error) {
      console.error('Error toggling service status:', error);
      showMessage('error', 'Failed to update service status');
    }
  };

  const openAddServiceModal = () => {
    setServiceForm({
      service_name: '',
      description: '',
      duration_minutes: 30,
      menu_category_id: activeMenuCategory,
      service_category_id: '',
      image_url: '',
      price: null,
      is_variable: false,
      variants: [],
      has_options: false,
      options: [],
      is_bookable: true,
      is_enabled: true,
      component_display_enabled: false
    });
    setImageFile(null);
    setVariantType('Duration'); // Reset variant type
    setVariants([]); // Reset variants
    setShowAddServiceModal(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setServiceForm({ ...serviceForm, image_url: data.image_url });
        setImageFile(null);
        showMessage('success', 'Image uploaded successfully');
      } else {
        showMessage('error', 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showMessage('error', 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAIGenerateDescription = async () => {
    if (!aiGenerateInput.name || !aiGenerateInput.description) {
      showMessage('error', 'Please provide both service name and rough description');
      return;
    }
    
    setGeneratingDescription(true);
    try {
      console.log('🤖 Generating AI description for:', aiGenerateInput.name);
      
      const response = await fetch(`${API_BASE_URL}/api/appointment-services/generate-description`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_name: aiGenerateInput.name,
          rough_description: aiGenerateInput.description
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI description generated:', data.description);
        
        setServiceForm(prevForm => ({
          ...prevForm,
          description: data.description
        }));
        
        setShowAIGenerateModal(false);
        setAIGenerateInput({ name: '', description: '' });
        showMessage('success', 'Description generated successfully!');
      } else {
        const errorData = await response.json();
        console.error('❌ Error from backend:', errorData);
        showMessage('error', errorData.detail || 'Failed to generate description');
      }
    } catch (error) {
      console.error('❌ Error generating description:', error);
      showMessage('error', 'Network error. Please check your connection and try again.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleAIGenerateImage = async () => {
    if (!aiImageInput.name) {
      setAIImageError('Please provide a service name');
      return;
    }

    setGeneratingImage(true);
    setAIImageError('');
    
    try {
      console.log('🎨 Generating AI image for:', aiImageInput.name);
      
      const response = await fetch(`${API_BASE_URL}/api/appointment-services/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_name: aiImageInput.name,
          service_description: aiImageInput.description,
          model: selectedImageModel
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI image generated:', data.image_url);
        
        setServiceForm(prevForm => ({
          ...prevForm,
          image_url: data.image_url,
          s3_key: data.s3_key
        }));
        
        setImageFile(null);
        setShowAIImageModal(false);
        setAIImageInput({ name: '', description: '' });
        setAIImageError('');
        showMessage('success', 'Service image generated successfully!');
      } else {
        const errorData = await response.json();
        console.error('❌ Error from backend:', errorData);
        setAIImageError(errorData.detail || 'Failed to generate image. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error generating image:', error);
      setAIImageError('Network error. Please check your connection and try again.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const fetchS3Images = async (searchQuery = '') => {
    if (!searchQuery || !searchQuery.trim()) {
      setS3Images([]);
      return;
    }
    
    setLoadingImages(true);
    try {
      const queryParam = searchQuery.trim();
      console.log('🔍 Searching S3 images with query:', queryParam);
      const response = await fetch(`${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(queryParam)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ S3 Images data received:', data);
        console.log('📸 Number of images:', data.images?.length || 0);
        setS3Images(data.images || []);
        if (!data.images || data.images.length === 0) {
          showMessage('info', `No images found matching "${queryParam}". Try AI Generate or Upload to add new images.`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        showMessage('error', `Failed to search images: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error searching S3 images:', error);
      showMessage('error', `Failed to search images: ${error.message}`);
    } finally {
      setLoadingImages(false);
    }
  };
  
  const handleSearchS3Images = () => {
    if (imageSearchQuery.trim().length < 3) {
      showMessage('warning', 'Please enter at least 3 characters to search');
      return;
    }
    setHasSearched(true);
    fetchS3Images(imageSearchQuery);
  };

  const selectS3Image = (imageUrl, s3Key) => {
    console.log('📸 Selecting S3 image:', imageUrl);
    console.log('🔑 S3 key:', s3Key);
    setServiceForm(prevForm => ({
      ...prevForm,
      image_url: imageUrl,
      s3_key: s3Key
    }));
    setImageFile(null);
    setShowImageBrowser(false);
    showMessage('success', 'Image selected successfully');
  };

  const addVariant = () => {
    setVariants([...variants, {
      variant_type: variantType,
      variant_name: '',
      price: 0,
      is_enabled: true
    }]);
  };

  const addDurationPresets = () => {
    const durationPresets = [
      { variant_type: 'Duration', variant_name: '15min', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '30min', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '45min', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '1hr', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '1hr 30min', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '2hr', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '4hr', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '8hr', price: 0, is_enabled: true },
      { variant_type: 'Duration', variant_name: '24hr', price: 0, is_enabled: true }
    ];
    setVariantType('Duration');
    setVariants(durationPresets);
  };

  const updateVariant = (variantIndex, field, value) => {
    const updated = [...variants];
    updated[variantIndex][field] = value;
    setVariants(updated);
  };

  const deleteVariant = (variantIndex) => {
    const updated = [...variants];
    updated.splice(variantIndex, 1);
    setVariants(updated);
  };

  const addOption = () => {
    const newOptions = [...(serviceForm.options || [])];
    newOptions.push({
      option_id: Date.now().toString(),
      option_name: '',
      option_type: 'single', // Default to single (add-on)
      price: '',
      choices: [],
      is_required: false
    });
    setServiceForm({ ...serviceForm, options: newOptions });
  };

  const updateOption = (index, field, value) => {
    const updated = [...serviceForm.options];
    updated[index][field] = value;
    setServiceForm({ ...serviceForm, options: updated });
  };

  const deleteOption = (index) => {
    const updated = [...serviceForm.options];
    updated.splice(index, 1);
    setServiceForm({ ...serviceForm, options: updated });
  };

  const openEditServiceModal = (service) => {
    setEditingService(service);
    setServiceForm({
      service_name: service.service_name,
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      menu_category_id: service.menu_category_id,
      service_category_id: service.service_category_id || '',
      image_url: service.image_url || '',
      price: service.price,
      is_variable: service.is_variable || false,
      variants: service.variants || [],
      has_options: service.has_options || false,
      options: service.options || [],
      is_bookable: service.is_bookable !== undefined ? service.is_bookable : true,
      is_enabled: service.is_enabled,
      component_display_enabled: service.component_display_enabled || false
    });
    
    // Load variants for editing
    if (service.variants && service.variants.length > 0) {
      // Get variant type from first variant
      setVariantType(service.variants[0].variant_type || 'Duration');
      setVariants(service.variants);
    } else {
      setVariantType('Duration');
      setVariants([]);
    }
    
    setImageFile(null);
    setShowEditServiceModal(true);
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      showMessage('error', 'Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('category_id', activeMenuCategory);

    try {
      const response = await fetch(`${API_BASE_URL}/api/appointment-services/import-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setCsvImportResult(result);
        showMessage('success', `Imported ${result.imported || 0} services successfully`);
        await fetchServices();
      } else {
        const error = await response.json();
        showMessage('error', error.detail || 'Failed to import CSV');
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      showMessage('error', 'Failed to import CSV');
    }
  };

  const handleCSVExport = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/appointment-services/export-csv?menu_category_id=${activeMenuCategory}`,
        { 
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `services-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showMessage('success', 'Services exported successfully');
      } else {
        showMessage('error', 'Failed to export services');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showMessage('error', 'Failed to export services');
    }
  };

  const handleDeleteAll = () => {
    setShowDeleteAllModal(true);
  };

  const confirmDeleteAll = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/appointment-services?menu_category_id=${activeMenuCategory}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        showMessage('success', 'All services deleted successfully');
        setShowDeleteAllModal(false);
        await fetchServices();
      } else {
        showMessage('error', 'Failed to delete services');
      }
    } catch (error) {
      console.error('Error deleting all services:', error);
      showMessage('error', 'Failed to delete services');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Message */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 mr-2" />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm mb-2">
          {/* Header with Title */}
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Service Management</h1>
          </div>

          {/* Tabs - Integrated with Header Card */}
          <div className="border-b border-gray-200">
            <nav className="flex items-center justify-between -mb-px px-4">
              <div className="flex">
                {menuCategories.map(category => {
                  const isActive = activeMenuCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveMenuCategory(category.id)}
                      className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center space-x-2 ${
                        isActive
                          ? 'border-theme-blue text-theme-blue'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Briefcase className="h-4 w-4" />
                      <span>{category.menu_category_name}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowManageCategoriesModal(true)}
                  className="py-3 px-4 text-gray-500 hover:text-theme-blue font-medium text-sm flex items-center space-x-2 transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  <span>Manage Categories</span>
                </button>
                <button
                  onClick={() => setShowCSVSection(!showCSVSection)}
                  className="py-3 px-3 text-gray-500 hover:text-theme-blue font-medium text-sm flex items-center transition-colors rounded-lg hover:bg-gray-50"
                  title="CSV Import/Export"
                >
                  <FileText className="h-4 w-4" />
                  {showCSVSection ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </button>
              </div>
            </nav>
          </div>
        </div>

        {/* Content Area */}
        {activeMenuCategory && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 space-y-3">

              {/* CSV Actions Bar - Collapsible with Smooth Animation */}
              <div 
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  showCSVSection ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setShowCSVImportModal(true)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Import CSV
                    </button>
                    <button
                      onClick={handleCSVExport}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </button>
                    <button
                      onClick={() => setShowCSVFormatModal(true)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      CSV Format
                    </button>
                    <button
                      onClick={handleDeleteAll}
                      className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors flex items-center"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All Items
                    </button>
                  </div>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-wrap gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* Service Category Filter Button */}
                <button
                  onClick={() => setShowCategoryOverlay(!showCategoryOverlay)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center text-sm font-medium text-gray-700"
                  title="Filter by Category"
                >
                  <Filter className="h-4 w-4" />
                </button>

                {/* Status Filter */}
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>

                {/* Manage Service Categories Button */}
                <button
                  onClick={() => setShowManageServiceCategoriesModal(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Service Categories
                </button>

                {/* Add Service Button */}
                <button
                  onClick={openAddServiceModal}
                  className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)] transition-colors flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </button>
              </div>

              {/* Services Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[rgb(41,173,211)] border-t-transparent mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading services...</p>
                </div>
              ) : filteredServices.length > 0 ? (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {currentServices.map((service) => (
                    <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex">
                      {/* Service Image - Left Side */}
                      <div className="relative w-48 flex-shrink-0">
                        {service.image_url ? (
                          <img
                            src={service.image_url}
                            alt={service.service_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <ImageIcon className="h-16 w-16 text-gray-400" />
                          </div>
                        )}
                        
                        {/* Component Display Badge - Top Right */}
                        {service.component_display_enabled && (
                          <span className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded shadow-sm bg-green-500 text-white flex items-center gap-1">
                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"/>
                            </svg>
                            CMS
                          </span>
                        )}
                        
                        {/* Duration Badge - Bottom Left */}
                        <span className="absolute bottom-2 left-2 px-2 py-1 text-xs font-medium rounded shadow-sm bg-gray-800 text-white flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {service.is_variable && service.variants && service.variants.length > 0 ? (
                            // Show variant duration range
                            <>
                              {service.variants[0].variant_name} - {service.variants[service.variants.length - 1].variant_name}
                            </>
                          ) : (
                            // Show fixed duration
                            `${service.duration_minutes} min`
                          )}
                        </span>
                      </div>

                      {/* Service Details - Right Side */}
                      <div className="flex-1 p-3 flex flex-col">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1.5">{service.service_name}</h3>
                        
                        {service.description && (
                          <p className="text-gray-600 text-xs mb-2 line-clamp-3 flex-1 leading-relaxed">{service.description}</p>
                        )}
                        
                        {/* Price Display - Only show base price if NOT variable */}
                        {service.price && !service.is_variable && (
                          <div className="mb-2">
                            <span className="text-sm font-bold text-gray-900">${service.price.toFixed(2)}</span>
                          </div>
                        )}
                        
                        {/* Variants Display (if variable pricing) - Show only price range */}
                        {service.is_variable && service.variants && service.variants.length > 0 && (
                          <div className="mb-2">
                            <span className="text-sm font-bold text-gray-900">
                              ${Math.min(...service.variants.map(v => v.price)).toFixed(2)} - ${Math.max(...service.variants.map(v => v.price)).toFixed(2)}
                            </span>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-2 mt-auto items-center">
                          <button
                            onClick={() => openEditServiceModal(service)}
                            className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                          >
                            <Edit className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </button>
                          
                          {/* Active/Inactive Toggle Switch */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-600 font-medium">
                              {service.is_enabled ? 'Active' : 'Inactive'}
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={service.is_enabled}
                                onChange={() => handleToggleServiceStatus(service)}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700">
                        <span>
                          Showing {startIndex + 1} to {Math.min(endIndex, filteredServices.length)} of {filteredServices.length} services
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={goToPreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                            <button
                              key={pageNumber}
                              onClick={() => goToPage(pageNumber)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                currentPage === pageNumber
                                  ? 'text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                              style={currentPage === pageNumber ? { backgroundColor: 'var(--theme-primary)' } : {}}
                            >
                              {pageNumber}
                            </button>
                          ))}
                        </div>
                        
                        <button
                          onClick={goToNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                </>
              ) : (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                  <p className="text-gray-600 mb-6">
                    {searchTerm || selectedServiceCategory !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Get started by adding your first service'}
                  </p>
                  {!searchTerm && selectedServiceCategory === 'all' && (
                    <button
                      onClick={openAddServiceModal}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                      Add Service
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Menu Categories State */}
        {!activeMenuCategory && !loading && menuCategories.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No service menu categories yet</h3>
            <p className="text-gray-600 mb-6">Create your first service menu category to get started</p>
            <button
              onClick={() => setShowManageCategoriesModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] transition-colors"
            >
              <Plus className="h-5 w-5" />
              Create Menu Category
            </button>
          </div>
        )}

        {/* Manage Menu Categories Modal */}
        {showManageCategoriesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header with cyan background */}
              <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-white" />
                  <h3 className="text-lg font-semibold text-white">Manage Menu Categories</h3>
                </div>
                <button
                  onClick={() => setShowManageCategoriesModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Existing Menu Categories */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Existing Menu Categories</h4>
                  {menuCategories.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No menu categories yet. Add your first category below.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {menuCategories.map(category => (
                        <div
                          key={category.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center flex-1">
                            <Briefcase className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {category.menu_category_name}
                                <span className="ml-2 text-xs text-gray-500">
                                  ({services.filter(s => s.menu_category_id === category.id).length})
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditMenuCategory(category)}
                              className="p-2 text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit category"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteMenuCategory(category)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete category"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Menu Category Form */}
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Add New Menu Category</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Name *
                      </label>
                      <input
                        type="text"
                        value={menuCategoryForm.menu_category_name}
                        onChange={(e) => setMenuCategoryForm({ ...menuCategoryForm, menu_category_name: e.target.value })}
                        placeholder="e.g., Veterinary Services, Grooming Services"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                    </div>

                    <button
                      onClick={handleAddMenuCategory}
                      className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center justify-center"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Menu Category
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manage Service Categories Modal */}
        {showManageServiceCategoriesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header with cyan background */}
              <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center">
                  <Package className="h-5 w-5 mr-2 text-white" />
                  <h3 className="text-lg font-semibold text-white">Manage Service Categories</h3>
                </div>
                <button
                  onClick={() => setShowManageServiceCategoriesModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Current Menu Category Info */}
                {activeMenuCategory && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Current Menu Category:</span>{' '}
                      {menuCategories.find(cat => cat.id === activeMenuCategory)?.menu_category_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Service categories will be created within this menu category
                    </p>
                  </div>
                )}

                {/* Existing Service Categories */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Existing Service Categories</h4>
                  {serviceCategories.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No service categories yet. Add your first category below.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {serviceCategories.map(category => (
                        <div
                          key={category.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center flex-1">
                            <Package className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {category.category_name}
                                <span className="ml-2 text-xs text-gray-500">
                                  ({services.filter(s => s.service_category_id === category.id).length})
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditServiceCategory(category)}
                              className="p-2 text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit category"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteServiceCategory(category)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete category"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Service Category Form */}
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Add New Service Category</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Category Name *
                      </label>
                      <input
                        type="text"
                        value={serviceCategoryForm.category_name}
                        onChange={(e) => setServiceCategoryForm({ ...serviceCategoryForm, category_name: e.target.value })}
                        placeholder="e.g., Grooming, Medical Exams, Vaccinations"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                    </div>

                    <button
                      onClick={handleAddServiceCategory}
                      className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center justify-center"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Service Category
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Service Modal - Enhanced */}
        {showAddServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full my-8">
              {/* Header */}
              <div className="sticky top-0 border-b border-gray-200 px-6 py-3 rounded-t-xl z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Add New Service</h2>
                  <button
                    onClick={() => setShowAddServiceModal(false)}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                
                {/* Service Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Haircut, Consultation, Spa Treatment"
                    value={serviceForm.service_name}
                    onChange={(e) => setServiceForm({ ...serviceForm, service_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Service Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Category *</label>
                  <select
                    value={serviceForm.service_category_id}
                    onChange={(e) => setServiceForm({ ...serviceForm, service_category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select a category</option>
                    {serviceCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description with AI Generate */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <button
                      type="button"
                      onClick={() => {
                        setAIGenerateInput({ name: serviceForm.service_name, description: '' });
                        setShowAIGenerateModal(true);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
                      style={{ backgroundColor: 'rgb(147, 51, 234)', color: 'white' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(126, 34, 206)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(147, 51, 234)'}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI Generate
                    </button>
                  </div>
                  <textarea
                    placeholder="Describe your service..."
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Service Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {imageFile || serviceForm.image_url ? (
                      <div className="space-y-3">
                        {imageFile && (
                          <div className="relative inline-block group">
                            <img
                              src={URL.createObjectURL(imageFile)}
                              alt="Preview"
                              className="w-40 h-40 object-contain mx-auto rounded bg-gray-50"
                            />
                            <button
                              onClick={() => {
                                setPreviewImageUrl(URL.createObjectURL(imageFile));
                                setShowImagePreview(true);
                              }}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                          </div>
                        )}
                        {!imageFile && serviceForm.image_url && (
                          <div className="relative inline-block group">
                            <img
                              key={serviceForm.image_url}
                              src={serviceForm.image_url}
                              alt="Current"
                              className="w-40 h-40 object-contain mx-auto rounded bg-gray-50"
                              onError={(e) => {
                                console.error('Image failed to load:', serviceForm.image_url);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('✅ Image loaded successfully:', serviceForm.image_url);
                              }}
                            />
                            <button
                              onClick={() => {
                                setPreviewImageUrl(serviceForm.image_url);
                                setShowImagePreview(true);
                              }}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            onClick={() => {
                              setServiceForm({ ...serviceForm, image_url: '' });
                              setImageFile(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Remove Image
                          </button>
                          <button
                            onClick={() => {
                              setShowImageBrowser(true);
                              setImageSearchQuery('');
                              setS3Images([]);
                              setHasSearched(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            Browse Images
                          </button>
                          <button
                            onClick={() => {
                              setAIImageInput({
                                name: serviceForm.service_name || '',
                                description: serviceForm.description || ''
                              });
                              setShowAIImageModal(true);
                              setAIImageError('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI Generate Image
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">Upload service image</p>
                        <div className="flex gap-2 justify-center flex-wrap">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                setImageFile(e.target.files[0]);
                              }
                            }}
                            className="hidden"
                            id="service-image-upload"
                          />
                          <label
                            htmlFor="service-image-upload"
                            className="inline-block px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            Upload Image
                          </label>
                          <button
                            onClick={() => {
                              setShowImageBrowser(true);
                              setImageSearchQuery('');
                              setS3Images([]);
                              setHasSearched(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            Browse Images
                          </button>
                          <button
                            onClick={() => {
                              setAIImageInput({
                                name: serviceForm.service_name || '',
                                description: serviceForm.description || ''
                              });
                              setShowAIImageModal(true);
                              setAIImageError('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI Generate
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Optional: Add an image to make your service more appealing to customers. Max 5MB.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

 

                {/* Duration - Hidden when using variants */}
                {!serviceForm.is_variable && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={serviceForm.duration_minutes}
                      onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: parseInt(e.target.value) || 30 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional - Leave as default if not applicable</p>
                  </div>
                )}

                {/* Variable Service Toggle */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Variable Service</h3>
                      <p className="text-xs text-gray-500">Add variants with different pricing (e.g., Basic/Premium, 30min/60min)</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={serviceForm.is_variable}
                        onChange={(e) => setServiceForm({ ...serviceForm, is_variable: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-blue"></div>
                    </label>
                  </div>

                  {serviceForm.is_variable && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                      {/* Variant Type Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variant Type Name</label>
                        <input
                          type="text"
                          placeholder="e.g., Duration, Service Tier, Package"
                          value={variantType}
                          onChange={(e) => setVariantType(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">This describes what varies (e.g., "Duration" for 30min/60min options)</p>
                      </div>

                      {/* Variants List */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">Variants</label>
                          {variantType === 'Duration' && variants.length === 0 && (
                            <button
                              onClick={addDurationPresets}
                              className="px-3 py-1 text-xs bg-blue-50 text-theme-blue rounded-md hover:bg-blue-100 transition-colors flex items-center gap-1"
                            >
                              <Clock className="h-3 w-3" />
                              Quick Add Duration Presets
                            </button>
                          )}
                        </div>
                        {variants.map((variant, vIdx) => (
                          <div key={vIdx} className="flex gap-2 items-center bg-gray-50 p-3 rounded-lg">
                            <input
                              type="text"
                              placeholder="Variant name (e.g., 30 minutes, Basic)"
                              value={variant.variant_name}
                              onChange={(e) => updateVariant(vIdx, 'variant_name', e.target.value)}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <input
                              type="number"
                              placeholder="Price"
                              step="0.01"
                              value={variant.price || ''}
                              onChange={(e) => updateVariant(vIdx, 'price', parseFloat(e.target.value) || 0)}
                              className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <button
                              onClick={() => deleteVariant(vIdx)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={addVariant}
                          className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-theme-blue transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Variant
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Base Price (when not variable) */}
                  {!serviceForm.is_variable && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Base Price</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={serviceForm.price || ''}
                        onChange={(e) => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || null })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Service Options Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Service Options
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Add customizable options like add-ons, durations, or preferences
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={serviceForm.has_options}
                        onChange={(e) => {
                          const hasOptions = e.target.checked;
                          setServiceForm({ 
                            ...serviceForm, 
                            has_options: hasOptions,
                            options: hasOptions ? [] : []
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={serviceForm.has_options ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>

                  {serviceForm.has_options && (
                    <div className="space-y-4 mt-4">
                      {/* Options List */}
                      {serviceForm.options && serviceForm.options.length > 0 && (
                        <div className="space-y-4">
                          {serviceForm.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="border border-gray-300 rounded-lg p-4 bg-white">
                              {/* Option Header */}
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">
                                  Option {optionIndex + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => deleteOption(optionIndex)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete option"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Option Name */}
                              <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Option Name *
                                </label>
                                <input
                                  type="text"
                                  value={option.option_name || ''}
                                  onChange={(e) => updateOption(optionIndex, 'option_name', e.target.value)}
                                  placeholder="e.g., Add Massage or Duration Extension"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                              </div>

                              {/* Option Type and Required Toggle */}
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Option Type *
                                  </label>
                                  <select
                                    value={option.option_type || 'single'}
                                    onChange={(e) => {
                                      const newOptions = [...serviceForm.options];
                                      newOptions[optionIndex].option_type = e.target.value;
                                      // Reset fields when switching type
                                      if (e.target.value === 'single') {
                                        newOptions[optionIndex].choices = undefined;
                                      } else {
                                        newOptions[optionIndex].price = undefined;
                                        newOptions[optionIndex].choices = [{ choice_id: Date.now().toString(), choice_name: '', price: '' }];
                                      }
                                      setServiceForm({ ...serviceForm, options: newOptions });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                  >
                                    <option value="single">Single (Add-on)</option>
                                    <option value="dropdown">Dropdown (Choices)</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Customer Selection
                                  </label>
                                  <div className="flex items-center h-10 px-3 border border-gray-200 rounded-lg bg-gray-50">
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={option.is_required || false}
                                        onChange={(e) => updateOption(optionIndex, 'is_required', e.target.checked)}
                                        className="mr-2"
                                      />
                                      <span className="text-xs text-gray-700">Required</span>
                                    </label>
                                  </div>
                                </div>
                              </div>

                              {/* Single Option Price */}
                              {option.option_type === 'single' && (
                                <div className="mb-3">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Price (Optional)
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={option.price || ''}
                                      onChange={(e) => updateOption(optionIndex, 'price', e.target.value)}
                                      placeholder="0.00"
                                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    e.g., +$15.00 for "Add Hot Stone Massage"
                                  </p>
                                </div>
                              )}

                              {/* Dropdown Choices */}
                              {option.option_type === 'dropdown' && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="block text-xs font-medium text-gray-700">
                                      Choices * (Max 5)
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentChoices = option.choices || [];
                                        if (currentChoices.length >= 5) {
                                          alert('Maximum 5 choices allowed per dropdown option');
                                          return;
                                        }
                                        const newOptions = [...serviceForm.options];
                                        newOptions[optionIndex].choices = [
                                          ...currentChoices,
                                          { choice_id: Date.now().toString(), choice_name: '', price: '' }
                                        ];
                                        setServiceForm({ ...serviceForm, options: newOptions });
                                      }}
                                      disabled={(option.choices || []).length >= 5}
                                      className="text-xs px-2 py-1 rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                    >
                                      + Add Choice
                                    </button>
                                  </div>

                                  <div className="space-y-2">
                                    {(option.choices || []).map((choice, choiceIndex) => (
                                      <div key={choice.choice_id || choiceIndex} className="flex gap-2 items-start">
                                        <div className="flex-1">
                                          <input
                                            type="text"
                                            value={choice.choice_name || ''}
                                            onChange={(e) => {
                                              const newOptions = [...serviceForm.options];
                                              newOptions[optionIndex].choices[choiceIndex].choice_name = e.target.value;
                                              setServiceForm({ ...serviceForm, options: newOptions });
                                            }}
                                            placeholder="e.g., Basic, Premium, Deluxe"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                          />
                                        </div>
                                        <div className="w-32">
                                          <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={choice.price || ''}
                                              onChange={(e) => {
                                                const newOptions = [...serviceForm.options];
                                                newOptions[optionIndex].choices[choiceIndex].price = e.target.value;
                                                setServiceForm({ ...serviceForm, options: newOptions });
                                              }}
                                              placeholder="0.00"
                                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOptions = [...serviceForm.options];
                                            newOptions[optionIndex].choices.splice(choiceIndex, 1);
                                            setServiceForm({ ...serviceForm, options: newOptions });
                                          }}
                                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Remove choice"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Option Button */}
                      <button
                        type="button"
                        onClick={addOption}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-theme-blue hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                      >
                        <Plus className="h-5 w-5" />
                        <span className="font-medium">Add Option</span>
                      </button>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 rounded-b-xl">
                <div className="flex items-center justify-between gap-4">
                  {/* Toggle Switches */}
                  <div className="flex items-center gap-6">
                    {/* Available Toggle */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 font-medium">Available</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serviceForm.is_available}
                          onChange={(e) => setServiceForm({ ...serviceForm, is_available: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={serviceForm.is_available ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>
                    
                    {/* Enabled Toggle */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 font-medium">Enabled</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serviceForm.is_enabled}
                          onChange={(e) => setServiceForm({ ...serviceForm, is_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={serviceForm.is_enabled ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>

                    {/* Component Display Toggle */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 font-medium">Component Display</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serviceForm.component_display_enabled}
                          onChange={(e) => setServiceForm({ ...serviceForm, component_display_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={serviceForm.component_display_enabled ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowAddServiceModal(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddService}
                      disabled={uploadingImage}
                      className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors disabled:bg-gray-400 text-sm"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      {uploadingImage ? 'Uploading...' : 'Add Service'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Service Modal - Enhanced (Same as Add but with Update button) */}
        {showEditServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full my-8">
              {/* Header */}
              <div className="sticky top-0 border-b border-gray-200 px-6 py-3 rounded-t-xl z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Edit Service</h2>
                  <button
                    onClick={() => setShowEditServiceModal(false)}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Content - Same as Add Modal */}
              <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                
                {/* Service Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Haircut, Consultation, Spa Treatment"
                    value={serviceForm.service_name}
                    onChange={(e) => setServiceForm({ ...serviceForm, service_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Service Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Category *</label>
                  <select
                    value={serviceForm.service_category_id}
                    onChange={(e) => setServiceForm({ ...serviceForm, service_category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select a category</option>
                    {serviceCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description with AI Generate */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <button
                      type="button"
                      onClick={() => {
                        setAIGenerateInput({ name: serviceForm.service_name, description: '' });
                        setShowAIGenerateModal(true);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
                      style={{ backgroundColor: 'rgb(147, 51, 234)', color: 'white' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(126, 34, 206)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(147, 51, 234)'}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI Generate
                    </button>
                  </div>
                  <textarea
                    placeholder="Describe your service..."
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Service Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {imageFile || serviceForm.image_url ? (
                      <div className="space-y-3">
                        {imageFile && (
                          <div className="relative inline-block group">
                            <img
                              src={URL.createObjectURL(imageFile)}
                              alt="Preview"
                              className="w-40 h-40 object-contain mx-auto rounded bg-gray-50"
                            />
                            <button
                              onClick={() => {
                                setPreviewImageUrl(URL.createObjectURL(imageFile));
                                setShowImagePreview(true);
                              }}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                          </div>
                        )}
                        {!imageFile && serviceForm.image_url && (
                          <div className="relative inline-block group">
                            <img
                              key={serviceForm.image_url}
                              src={serviceForm.image_url}
                              alt="Current"
                              className="w-40 h-40 object-contain mx-auto rounded bg-gray-50"
                              onError={(e) => {
                                console.error('Image failed to load:', serviceForm.image_url);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('✅ Image loaded successfully:', serviceForm.image_url);
                              }}
                            />
                            <button
                              onClick={() => {
                                setPreviewImageUrl(serviceForm.image_url);
                                setShowImagePreview(true);
                              }}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            onClick={() => {
                              setServiceForm({ ...serviceForm, image_url: '' });
                              setImageFile(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Remove Image
                          </button>
                          <button
                            onClick={() => {
                              setShowImageBrowser(true);
                              setImageSearchQuery('');
                              setS3Images([]);
                              setHasSearched(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            Browse Images
                          </button>
                          <button
                            onClick={() => {
                              setAIImageInput({
                                name: serviceForm.service_name || '',
                                description: serviceForm.description || ''
                              });
                              setShowAIImageModal(true);
                              setAIImageError('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI Generate Image
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">Upload service image</p>
                        <div className="flex gap-2 justify-center flex-wrap">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                setImageFile(e.target.files[0]);
                              }
                            }}
                            className="hidden"
                            id="service-image-upload"
                          />
                          <label
                            htmlFor="service-image-upload"
                            className="inline-block px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            Upload Image
                          </label>
                          <button
                            onClick={() => {
                              setShowImageBrowser(true);
                              setImageSearchQuery('');
                              setS3Images([]);
                              setHasSearched(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            Browse Images
                          </button>
                          <button
                            onClick={() => {
                              setAIImageInput({
                                name: serviceForm.service_name || '',
                                description: serviceForm.description || ''
                              });
                              setShowAIImageModal(true);
                              setAIImageError('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI Generate
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Optional: Add an image to make your service more appealing to customers. Max 5MB.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

 

                {/* Duration - Hidden when using variants */}
                {!serviceForm.is_variable && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={serviceForm.duration_minutes}
                      onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: parseInt(e.target.value) || 30 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional - Leave as default if not applicable</p>
                  </div>
                )}

                {/* Variable Service Toggle */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Variable Service</h3>
                      <p className="text-xs text-gray-500">Add variants with different pricing (e.g., Basic/Premium, 30min/60min)</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={serviceForm.is_variable}
                        onChange={(e) => setServiceForm({ ...serviceForm, is_variable: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-blue"></div>
                    </label>
                  </div>

                  {serviceForm.is_variable && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                      {/* Variant Type Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variant Type Name</label>
                        <input
                          type="text"
                          placeholder="e.g., Duration, Service Tier, Package"
                          value={variantType}
                          onChange={(e) => setVariantType(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">This describes what varies (e.g., "Duration" for 30min/60min options)</p>
                      </div>

                      {/* Variants List */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">Variants</label>
                          {variantType === 'Duration' && variants.length === 0 && (
                            <button
                              onClick={addDurationPresets}
                              className="px-3 py-1 text-xs bg-blue-50 text-theme-blue rounded-md hover:bg-blue-100 transition-colors flex items-center gap-1"
                            >
                              <Clock className="h-3 w-3" />
                              Quick Add Duration Presets
                            </button>
                          )}
                        </div>
                        {variants.map((variant, vIdx) => (
                          <div key={vIdx} className="flex gap-2 items-center bg-gray-50 p-3 rounded-lg">
                            <input
                              type="text"
                              placeholder="Variant name (e.g., 30 minutes, Basic)"
                              value={variant.variant_name}
                              onChange={(e) => updateVariant(vIdx, 'variant_name', e.target.value)}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <input
                              type="number"
                              placeholder="Price"
                              step="0.01"
                              value={variant.price || ''}
                              onChange={(e) => updateVariant(vIdx, 'price', parseFloat(e.target.value) || 0)}
                              className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <button
                              onClick={() => deleteVariant(vIdx)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={addVariant}
                          className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-theme-blue transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Variant
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Base Price (when not variable) */}
                  {!serviceForm.is_variable && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Base Price</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={serviceForm.price || ''}
                        onChange={(e) => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || null })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Service Options Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Service Options
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Add customizable options like add-ons, durations, or preferences
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={serviceForm.has_options}
                        onChange={(e) => {
                          const hasOptions = e.target.checked;
                          setServiceForm({ 
                            ...serviceForm, 
                            has_options: hasOptions,
                            options: hasOptions ? [] : []
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={serviceForm.has_options ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>

                  {serviceForm.has_options && (
                    <div className="space-y-4 mt-4">
                      {/* Options List */}
                      {serviceForm.options && serviceForm.options.length > 0 && (
                        <div className="space-y-4">
                          {serviceForm.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="border border-gray-300 rounded-lg p-4 bg-white">
                              {/* Option Header */}
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">
                                  Option {optionIndex + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => deleteOption(optionIndex)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete option"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Option Name */}
                              <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Option Name *
                                </label>
                                <input
                                  type="text"
                                  value={option.option_name || ''}
                                  onChange={(e) => updateOption(optionIndex, 'option_name', e.target.value)}
                                  placeholder="e.g., Add Massage or Duration Extension"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                              </div>

                              {/* Option Type and Required Toggle */}
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Option Type *
                                  </label>
                                  <select
                                    value={option.option_type || 'single'}
                                    onChange={(e) => {
                                      const newOptions = [...serviceForm.options];
                                      newOptions[optionIndex].option_type = e.target.value;
                                      // Reset fields when switching type
                                      if (e.target.value === 'single') {
                                        newOptions[optionIndex].choices = undefined;
                                      } else {
                                        newOptions[optionIndex].price = undefined;
                                        newOptions[optionIndex].choices = [{ choice_id: Date.now().toString(), choice_name: '', price: '' }];
                                      }
                                      setServiceForm({ ...serviceForm, options: newOptions });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                  >
                                    <option value="single">Single (Add-on)</option>
                                    <option value="dropdown">Dropdown (Choices)</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Customer Selection
                                  </label>
                                  <div className="flex items-center h-10 px-3 border border-gray-200 rounded-lg bg-gray-50">
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={option.is_required || false}
                                        onChange={(e) => updateOption(optionIndex, 'is_required', e.target.checked)}
                                        className="mr-2"
                                      />
                                      <span className="text-xs text-gray-700">Required</span>
                                    </label>
                                  </div>
                                </div>
                              </div>

                              {/* Single Option Price */}
                              {option.option_type === 'single' && (
                                <div className="mb-3">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Price (Optional)
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={option.price || ''}
                                      onChange={(e) => updateOption(optionIndex, 'price', e.target.value)}
                                      placeholder="0.00"
                                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    e.g., +$15.00 for "Add Hot Stone Massage"
                                  </p>
                                </div>
                              )}

                              {/* Dropdown Choices */}
                              {option.option_type === 'dropdown' && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="block text-xs font-medium text-gray-700">
                                      Choices * (Max 5)
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentChoices = option.choices || [];
                                        if (currentChoices.length >= 5) {
                                          alert('Maximum 5 choices allowed per dropdown option');
                                          return;
                                        }
                                        const newOptions = [...serviceForm.options];
                                        newOptions[optionIndex].choices = [
                                          ...currentChoices,
                                          { choice_id: Date.now().toString(), choice_name: '', price: '' }
                                        ];
                                        setServiceForm({ ...serviceForm, options: newOptions });
                                      }}
                                      disabled={(option.choices || []).length >= 5}
                                      className="text-xs px-2 py-1 rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                    >
                                      + Add Choice
                                    </button>
                                  </div>

                                  <div className="space-y-2">
                                    {(option.choices || []).map((choice, choiceIndex) => (
                                      <div key={choice.choice_id || choiceIndex} className="flex gap-2 items-start">
                                        <div className="flex-1">
                                          <input
                                            type="text"
                                            value={choice.choice_name || ''}
                                            onChange={(e) => {
                                              const newOptions = [...serviceForm.options];
                                              newOptions[optionIndex].choices[choiceIndex].choice_name = e.target.value;
                                              setServiceForm({ ...serviceForm, options: newOptions });
                                            }}
                                            placeholder="e.g., Basic, Premium, Deluxe"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                          />
                                        </div>
                                        <div className="w-32">
                                          <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={choice.price || ''}
                                              onChange={(e) => {
                                                const newOptions = [...serviceForm.options];
                                                newOptions[optionIndex].choices[choiceIndex].price = e.target.value;
                                                setServiceForm({ ...serviceForm, options: newOptions });
                                              }}
                                              placeholder="0.00"
                                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOptions = [...serviceForm.options];
                                            newOptions[optionIndex].choices.splice(choiceIndex, 1);
                                            setServiceForm({ ...serviceForm, options: newOptions });
                                          }}
                                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Remove choice"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Option Button */}
                      <button
                        type="button"
                        onClick={addOption}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-theme-blue hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                      >
                        <Plus className="h-5 w-5" />
                        <span className="font-medium">Add Option</span>
                      </button>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 rounded-b-xl">
                <div className="flex items-center justify-between gap-4">
                  {/* Left Side - Delete Button */}
                  <button
                    onClick={() => handleDeleteService(editingService)}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Service
                  </button>
                  
                  {/* Right Side - Toggles and Update Button */}
                  <div className="flex items-center gap-3">
                    {/* Available Toggle */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 font-medium">Available</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serviceForm.is_bookable}
                          onChange={(e) => setServiceForm({ ...serviceForm, is_bookable: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={serviceForm.is_bookable ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>
                    
                    {/* Enabled Toggle */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 font-medium">Enabled</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serviceForm.is_enabled}
                          onChange={(e) => setServiceForm({ ...serviceForm, is_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={serviceForm.is_enabled ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>

                    {/* Component Display Toggle */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 font-medium">Component Display</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serviceForm.component_display_enabled}
                          onChange={(e) => setServiceForm({ ...serviceForm, component_display_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={serviceForm.component_display_enabled ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>
                    
                    <button
                      onClick={handleUpdateService}
                      disabled={uploadingImage}
                      className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors disabled:bg-gray-400 text-sm"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      {uploadingImage ? 'Uploading...' : 'Update Service'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSV Import Modal */}
        {showCSVImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">Import Services from CSV</h2>
                  <button
                    onClick={() => {
                      setShowCSVImportModal(false);
                      setCsvFile(null);
                      setCsvImportResult(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                  />
                </div>

                {csvImportResult && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Import Results</h4>
                    <p className="text-sm text-blue-800">
                      Imported: {csvImportResult.imported || 0} services<br />
                      Failed: {csvImportResult.failed || 0} services
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowCSVImportModal(false);
                      setCsvFile(null);
                      setCsvImportResult(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCSVImport}
                    disabled={!csvFile}
                    className="flex-1 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Import
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSV Format Modal */}
        {showCSVFormatModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">CSV Import Format</h2>
                  <button
                    onClick={() => setShowCSVFormatModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  Your CSV file should have the following columns:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-4 overflow-x-auto">
                  <pre className="text-sm text-gray-800">
service_name,description,duration_minutes,is_enabled
Haircut,Professional haircut service,30,true
Beard Trim,Professional beard trimming,15,true
Hair Color,Hair coloring service,90,true
                  </pre>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <p><strong>service_name</strong> (required): Name of the service</p>
                  <p><strong>description</strong> (optional): Description of the service</p>
                  <p><strong>duration_minutes</strong> (required): Duration in minutes</p>
                  <p><strong>is_enabled</strong> (optional): true or false (default: true)</p>
                </div>
                <button
                  onClick={() => setShowCSVFormatModal(false)}
                  className="mt-6 w-full px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Service Modal */}
        {showDeleteServiceModal && serviceToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete Service</h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete the service <span className="font-semibold">"{serviceToDelete.service_name}"</span>? 
                This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteServiceModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteService}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Service
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Services Modal */}
        {showDeleteAllModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete All Services</h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>all services</strong> in this menu category? 
                This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteAllModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAll}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Menu Category Confirmation Modal */}
        {showDeleteMenuCategoryModal && menuCategoryToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete Menu Category</h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete the menu category <span className="font-semibold">"{menuCategoryToDelete.menu_category_name}"</span>? 
                This will also delete all service categories and services within it. This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteMenuCategoryModal(false);
                    setMenuCategoryToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteMenuCategory}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Service Category Confirmation Modal */}
        {showDeleteServiceCategoryModal && serviceCategoryToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete Service Category</h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete the service category <span className="font-semibold">"{serviceCategoryToDelete.category_name}"</span>? 
                This will also delete all services within this category. This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteServiceCategoryModal(false);
                    setServiceCategoryToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteServiceCategory}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Menu Category Modal */}
        {showEditMenuCategoryModal && editingMenuCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center">
                  <Edit className="h-5 w-5 mr-2 text-white" />
                  <h3 className="text-lg font-semibold text-white">Edit Menu Category</h3>
                </div>
                <button
                  onClick={() => {
                    setShowEditMenuCategoryModal(false);
                    setEditingMenuCategory(null);
                    setMenuCategoryForm({ menu_category_name: '', is_enabled: true, order: 0 });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={menuCategoryForm.menu_category_name}
                    onChange={(e) => setMenuCategoryForm({ ...menuCategoryForm, menu_category_name: e.target.value })}
                    placeholder="e.g., Veterinary Services"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-menu-cat-enabled"
                    checked={menuCategoryForm.is_enabled}
                    onChange={(e) => setMenuCategoryForm({ ...menuCategoryForm, is_enabled: e.target.checked })}
                    className="w-4 h-4 text-theme-blue rounded focus:ring-blue-500"
                  />
                  <label htmlFor="edit-menu-cat-enabled" className="text-sm text-gray-700">
                    Enable this category
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowEditMenuCategoryModal(false);
                      setEditingMenuCategory(null);
                      setMenuCategoryForm({ menu_category_name: '', is_enabled: true, order: 0 });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateMenuCategory}
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    Update Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Service Category Modal */}
        {showEditServiceCategoryModal && editingServiceCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center">
                  <Edit className="h-5 w-5 mr-2 text-white" />
                  <h3 className="text-lg font-semibold text-white">Edit Service Category</h3>
                </div>
                <button
                  onClick={() => {
                    setShowEditServiceCategoryModal(false);
                    setEditingServiceCategory(null);
                    setServiceCategoryForm({ menu_category_id: '', category_name: '', is_enabled: true, order: 0 });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={serviceCategoryForm.category_name}
                    onChange={(e) => setServiceCategoryForm({ ...serviceCategoryForm, category_name: e.target.value })}
                    placeholder="e.g., Grooming, Medical Exams"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-service-cat-enabled"
                    checked={serviceCategoryForm.is_enabled}
                    onChange={(e) => setServiceCategoryForm({ ...serviceCategoryForm, is_enabled: e.target.checked })}
                    className="w-4 h-4 text-theme-blue rounded focus:ring-blue-500"
                  />
                  <label htmlFor="edit-service-cat-enabled" className="text-sm text-gray-700">
                    Enable this category
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowEditServiceCategoryModal(false);
                      setEditingServiceCategory(null);
                      setServiceCategoryForm({ menu_category_id: '', category_name: '', is_enabled: true, order: 0 });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateServiceCategory}
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    Update Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Browser Modal */}
        {showImageBrowser && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-semibold text-white">Browse Images</h3>
                <button
                  onClick={() => setShowImageBrowser(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* Search Box */}
                <div className="mb-6">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={imageSearchQuery}
                      onChange={(e) => setImageSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && imageSearchQuery.trim().length >= 3) {
                          handleSearchS3Images();
                        }
                      }}
                      placeholder="Enter at least 3 characters to search (e.g., 'chicken', 'pizza')"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <button
                      onClick={handleSearchS3Images}
                      disabled={loadingImages || imageSearchQuery.trim().length < 3}
                      className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      {loadingImages ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Tip: Enter at least 3 characters to enable search. If image not found, use "AI Generate" or upload option.
                  </p>
                </div>

                {/* Results */}
                {loadingImages ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                    <p className="text-gray-500 mt-4">Searching images...</p>
                  </div>
                ) : !hasSearched ? (
                  <div className="text-center py-12">
                    {/* Empty state - no icon, just clean space */}
                  </div>
                ) : s3Images.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">No images found matching "{imageSearchQuery}"</p>
                    <p className="text-sm text-gray-500 mt-2">Try a different search term or:</p>
                    <div className="mt-4 flex gap-3 justify-center">
                      <button
                        onClick={() => {
                          setShowImageBrowser(false);
                          setAIImageInput({
                            name: serviceForm.service_name || '',
                            description: serviceForm.description || ''
                          });
                          setShowAIImageModal(true);
                        }}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                        style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                      >
                        🎨 AI Generate Image
                      </button>
                      <label
                        htmlFor="image-upload-browser"
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        📤 Upload Image
                      </label>
                      <input
                        type="file"
                        id="image-upload-browser"
                        accept="image/*"
                        onChange={(e) => {
                          setImageFile(e.target.files[0]);
                          setShowImageBrowser(false);
                        }}
                        className="hidden"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {s3Images.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => selectS3Image(image.url, image.key)}
                        className="relative border-2 border-gray-200 rounded-lg overflow-hidden hover:border-theme-blue cursor-pointer transition-all group"
                      >
                        <img
                          src={image.url}
                          alt={image.filename}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <CheckCircle className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs text-gray-600 truncate" title={image.filename}>
                            {image.filename}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowImageBrowser(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* AI Generate Description Modal */}
        {showAIGenerateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-semibold text-white">AI Generate Description</h3>
                <button
                  onClick={() => {
                    setShowAIGenerateModal(false);
                    setAIGenerateInput({ name: '', description: '' });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={aiGenerateInput.name}
                    onChange={(e) => setAIGenerateInput({ ...aiGenerateInput, name: e.target.value })}
                    placeholder="e.g., Pet Grooming, Health Checkup"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rough Description *
                  </label>
                  <textarea
                    value={aiGenerateInput.description}
                    onChange={(e) => setAIGenerateInput({ ...aiGenerateInput, description: e.target.value })}
                    placeholder="Brief description of what this service includes..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowAIGenerateModal(false);
                      setAIGenerateInput({ name: '', description: '' });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAIGenerateDescription}
                    disabled={generatingDescription}
                    className="flex-1 px-4 py-2 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {generatingDescription ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Generate Image Modal */}
        {showAIImageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-semibold text-white">AI Generate Service Image</h3>
                <button
                  onClick={() => {
                    setShowAIImageModal(false);
                    setAIImageInput({ name: '', description: '' });
                    setAIImageError('');
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {aiImageError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {aiImageError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={aiImageInput.name}
                    onChange={(e) => setAIImageInput({ ...aiImageInput, name: e.target.value })}
                    placeholder="e.g., Pet Grooming, Health Checkup"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={aiImageInput.description}
                    onChange={(e) => setAIImageInput({ ...aiImageInput, description: e.target.value })}
                    placeholder="Additional details to help generate a better image..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {availableImageModels.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image Model
                    </label>
                    <select
                      value={selectedImageModel}
                      onChange={(e) => setSelectedImageModel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {availableImageModels.map(model => (
                        <option key={model.id} value={model.id}>
                          {model.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowAIImageModal(false);
                      setAIImageInput({ name: '', description: '' });
                      setAIImageError('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAIGenerateImage}
                    disabled={generatingImage}
                    className="flex-1 px-4 py-2 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {generatingImage ? 'Generating...' : 'Generate Image'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
            onClick={() => setShowImagePreview(false)}
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <img
                src={previewImageUrl}
                alt="Preview"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setShowImagePreview(false)}
                className="absolute top-3 right-3 p-2 bg-black bg-opacity-60 rounded hover:bg-opacity-80 transition-all"
                type="button"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        )}

        {/* Category Filter Overlay */}
        {showCategoryOverlay && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[60vh] overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Filter by Category</h3>
                <button
                  onClick={() => setShowCategoryOverlay(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <div className="p-2 overflow-y-auto max-h-[calc(60vh-48px)]">
                <button
                  onClick={() => {
                    setSelectedServiceCategory('all');
                    setShowCategoryOverlay(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors flex items-center justify-between text-sm ${
                    selectedServiceCategory === 'all'
                      ? 'bg-[rgb(41,173,211)] text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="font-medium">All Service Categories</span>
                  <span className={`text-xs ${
                    selectedServiceCategory === 'all' ? 'text-white' : 'text-gray-500'
                  }`}>
                    ({services.length})
                  </span>
                </button>
                {serviceCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedServiceCategory(cat.id);
                      setShowCategoryOverlay(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors flex items-center justify-between text-sm ${
                      selectedServiceCategory === cat.id
                        ? 'bg-[rgb(41,173,211)] text-white'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className="font-medium">{cat.category_name}</span>
                    <span className={`text-xs ${
                      selectedServiceCategory === cat.id ? 'text-white' : 'text-gray-500'
                    }`}>
                      ({services.filter(s => s.service_category_id === cat.id).length})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceManagement;
